import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
} from '@angular/common/http';
import { OperatorService } from '../services/operator/operator.service';
import { Observable, of } from 'rxjs';
import {
  ApiResponsePublishedTournaments,
  GAME_STATS,
  INVENTORY,
  PRACTICE_ROUNDS,
  PublishedTournament,
  ROOMS,
  TIERS,
  TOURNAMENT_PUBLISHED,
  TOURNAMENTS,
  WAITING_OPPONENTS,
} from '../api/wager-api';
import { TIERS_MOCK } from './mock-responses/tiers.mock';
import { PRACTICE_ROUNDS_SOLITAIRE_MOCK } from './mock-responses/practice-rounds-solitaire.mock';
import { PRACTICE_ROUNDS_BLOCKS_MOCK } from './mock-responses/practice-rounds-blocks.mock';
import { PRACTICE_ROUNDS_BLACKJACK_MOCK } from './mock-responses/practice-rounds-blackjack.mock';
import { WAITING_OPPONENTS_MOCK } from './mock-responses/waiting-opponents.mock';
import {
  ACTIVITY_FEED_BLACKJACK_MOCK,
  ACTIVITY_FEED_BLOCKS_MOCK,
  ACTIVITY_FEED_SOLITAIRE_MOCK,
  ACTIVITY_FEED_TWENTYONE_MOCK,
  ActivityFeedMock,
} from './mock-responses/activiy-feed.mock';
import {
  LEADERBOARD_MOSTMONEY_1DAY,
  LEADERBOARD_MOSTMONEY_30DAY,
  LEADERBOARD_MOSTMONEY_7DAY,
  LEADERBOARD_MOSTMONEY_ALLTIME,
  LEADERBOARD_MOSTWINS_1DAY,
  LEADERBOARD_MOSTWINS_30DAY,
  LEADERBOARD_MOSTWINS_7DAY,
  LEADERBOARD_MOSTWINS_ALLTIME,
} from './mock-responses/leaderboards.mock';
import {
  LeaderboardPeriodType,
  LeaderboardType,
} from '../services/leaderboards/models/leaderboard-metadata';
import { INVENTORY_MOCK } from './mock-responses/inventory.mock';
import { GAME_STATS_MOCK } from './mock-responses/game-stats.mock';
import { PRACTICE_ROUNDS_TWENTYONE_MOCK } from './mock-responses/practice-rounds-twentyone.mock';
import { WAGER_ENDPOINTS } from '../api/wager/wager-api';

@Injectable()
export class DemoInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (OperatorService.IsDemo) {
      try {
        return of(this.demoResponseForUrl(request.url));
      } catch (error) {
        return next.handle(request);
      }
    } else if (this.isSandboxRequest(request.url)) {
      try {
        return of(this.sandboxResponseForUrl(request.url));
      } catch (error) {
        return next.handle(request);
      }
    }

    return next.handle(request);
  }

  private isSandboxRequest(url: string): boolean {
    return url.toLowerCase().includes('sandbox');
  }

  private demoResponseForUrl(url: string) {
    if (url.includes(TOURNAMENT_PUBLISHED)) {
      const publishedTournamentMock = new Array<PublishedTournament>();
      const tournamentsMock: ApiResponsePublishedTournaments = {
        status: 200,
        msg: 'Tournaments published',
        data: publishedTournamentMock,
      };
      return new HttpResponse({
        status: 200,
        body: tournamentsMock,
      });
    }
    if (url.includes(GAME_STATS)) {
      return new HttpResponse(GAME_STATS_MOCK);
    }
    if (url.includes(TIERS)) {
      return new HttpResponse(TIERS_MOCK);
    }
    if (url.includes(WAITING_OPPONENTS)) {
      return new HttpResponse(WAITING_OPPONENTS_MOCK);
    }
    if (url.includes(INVENTORY)) {
      return new HttpResponse(INVENTORY_MOCK);
    }
    if (url.includes(ROOMS) && url.includes(TOURNAMENTS)) {
      return new HttpResponse({
        status: 200,
        body: {
          status: 200,
          msg: 'Room tournaments',
          data: undefined,
        },
      });
    }
    if (url.includes(WAGER_ENDPOINTS.GAME_LEADERBOARDS)) {
      return this.getLeaderboardResponse(url);
    }
    if (url.includes(WAGER_ENDPOINTS.ACTIVITY_FEED)) {
      return this.getActivityFeedResponse(url);
    }
    if (url.includes(PRACTICE_ROUNDS)) {
      return this.getPracticeRoundsResponse(url);
    }

    throw new Error(`No mock response for url: ${url}`);
  }

  private sandboxResponseForUrl(url: string) {
    if (url.includes(WAGER_ENDPOINTS.GAME_LEADERBOARDS)) {
      return this.getLeaderboardResponse(url);
    }
    if (url.includes(WAGER_ENDPOINTS.ACTIVITY_FEED)) {
      return this.getActivityFeedResponse(url);
    }
    if (url.includes(GAME_STATS)) {
      return new HttpResponse(GAME_STATS_MOCK);
    }

    throw new Error(`No sandbox response for url: ${url}`);
  }

  private getLeaderboardResponse(url: string) {
    if (url.includes(LeaderboardType.MostWins)) {
      if (url.includes(LeaderboardPeriodType.Daily)) {
        return new HttpResponse(LEADERBOARD_MOSTWINS_1DAY);
      }
      if (url.includes(LeaderboardPeriodType.Weekly)) {
        return new HttpResponse(LEADERBOARD_MOSTWINS_7DAY);
      }
      if (url.includes(LeaderboardPeriodType.Monthly)) {
        return new HttpResponse(LEADERBOARD_MOSTWINS_30DAY);
      }
      if (url.includes(LeaderboardPeriodType.AllTime)) {
        return new HttpResponse(LEADERBOARD_MOSTWINS_ALLTIME);
      }
    } else if (url.includes(LeaderboardType.MostMoneyWon)) {
      if (url.includes(LeaderboardPeriodType.Daily)) {
        return new HttpResponse(LEADERBOARD_MOSTMONEY_1DAY);
      }
      if (url.includes(LeaderboardPeriodType.Weekly)) {
        return new HttpResponse(LEADERBOARD_MOSTMONEY_7DAY);
      }
      if (url.includes(LeaderboardPeriodType.Monthly)) {
        return new HttpResponse(LEADERBOARD_MOSTMONEY_30DAY);
      }
      if (url.includes(LeaderboardPeriodType.AllTime)) {
        return new HttpResponse(LEADERBOARD_MOSTMONEY_ALLTIME);
      }
    }
    throw new Error(`No leaderboard response for url: ${url}`);
  }

  private getActivityFeedResponse(url: string) {
    let activityMock: ActivityFeedMock | undefined = undefined;
    if (url.includes('solitaire')) {
      activityMock = ACTIVITY_FEED_SOLITAIRE_MOCK;
    } else if (url.includes('blocks')) {
      activityMock = ACTIVITY_FEED_BLOCKS_MOCK;
    } else if (url.includes('blackjack')) {
      activityMock = ACTIVITY_FEED_BLACKJACK_MOCK;
    } else if (url.includes('twentyone')) {
      activityMock = ACTIVITY_FEED_TWENTYONE_MOCK;
    }
    if (activityMock) {
      this.updateActivityFeedDates(activityMock);
      return new HttpResponse(activityMock);
    }
    throw new Error(`No activity feed response for url: ${url}`);
  }

  private getPracticeRoundsResponse(url: string) {
    if (url.includes('solitaire')) {
      const randomIndex = this.getRandomIndex(PRACTICE_ROUNDS_SOLITAIRE_MOCK);
      return new HttpResponse(PRACTICE_ROUNDS_SOLITAIRE_MOCK[randomIndex]);
    } else if (url.includes('blocks')) {
      const randomIndex = this.getRandomIndex(PRACTICE_ROUNDS_BLOCKS_MOCK);
      return new HttpResponse(PRACTICE_ROUNDS_BLOCKS_MOCK[randomIndex]);
    } else if (url.includes('blackjack')) {
      const randomIndex = this.getRandomIndex(PRACTICE_ROUNDS_BLACKJACK_MOCK);
      return new HttpResponse(PRACTICE_ROUNDS_BLACKJACK_MOCK[randomIndex]);
    } else if (url.includes('twentyone')) {
      const randomIndex = this.getRandomIndex(PRACTICE_ROUNDS_TWENTYONE_MOCK);
      return new HttpResponse(PRACTICE_ROUNDS_TWENTYONE_MOCK[randomIndex]);
    }
    throw new Error(`No practice rounds response for url: ${url}`);
  }

  private getRandomIndex<T>(array: T[]): number {
    return Math.floor(Math.random() * array.length);
  }

  /**
   * Iterates over each activity record and updates its createdAt timestamp
   * to have today's date while keeping the original time.
   */
  private updateActivityFeedDates(activityFeed: ActivityFeedMock): void {
    if (activityFeed && activityFeed.body.data && activityFeed.body.data.activities) {
      let minutesAgo = 1;
      Object.keys(activityFeed.body.data.activities).forEach((timeKey) => {
        const activityGroup = activityFeed.body.data.activities[timeKey];
        Object.keys(activityGroup).forEach((activityKey) => {
          const activity = activityGroup[activityKey];
          if (activity.createdAt) {
            const now = new Date();
            const updatedDate = new Date(now.getTime() - minutesAgo * 60 * 1000);
            activity.createdAt = updatedDate.getTime();
            minutesAgo++;
          }
        });
      });
    }
  }
}
